import { DataExport, ChatSession, ApiProvider, ChatTemplate } from '@/types';

/**
 * 数据导出工具类
 * 提供对话数据的导入、导出、备份和恢复功能
 */
export class DataExportUtil {
  /**
   * 导出对话数据
   */
  static exportData(
    providers: ApiProvider[],
    sessions: ChatSession[],
    templates: ChatTemplate[],
    options?: {
      includeProviders?: boolean;
      includeSessions?: boolean;
      includeTemplates?: boolean;
      sessionIds?: string[];
    }
  ): DataExport {
    const {
      includeProviders = true,
      includeSessions = true,
      includeTemplates = true,
      sessionIds,
    } = options || {};
    
    // 过滤会话（如果指定了sessionIds）
    let sessionsToExport = sessions;
    if (sessionIds && sessionIds.length > 0) {
      sessionsToExport = sessions.filter(session => sessionIds.includes(session.id));
    }
    
    // 过滤供应商（只导出与导出会话相关的供应商）
    let providersToExport: ApiProvider[] = [];
    if (includeProviders) {
      const providerIds = new Set(sessionsToExport.map(session => session.providerId));
      providersToExport = providers.filter(provider => providerIds.has(provider.id));
    }
    
    // 构建导出数据
    const exportData: DataExport = {
      version: '1.0.0',
      exportedAt: new Date().toISOString(),
      providers: includeProviders ? providersToExport : [],
      sessions: includeSessions ? sessionsToExport : [],
      templates: includeTemplates ? templates : [],
    };
    
    return exportData;
  }
  
  /**
   * 导出数据为JSON字符串
   */
  static exportToJson(
    providers: ApiProvider[],
    sessions: ChatSession[],
    templates: ChatTemplate[],
    options?: any
  ): string {
    const exportData = this.exportData(providers, sessions, templates, options);
    return JSON.stringify(exportData, null, 2);
  }
  
  /**
   * 导出数据并下载为文件
   */
  static exportToFile(
    providers: ApiProvider[],
    sessions: ChatSession[],
    templates: ChatTemplate[],
    filename?: string,
    options?: any
  ): void {
    const json = this.exportToJson(providers, sessions, templates, options);
    const dataStr = `data:text/json;charset=utf-8,${encodeURIComponent(json)}`;
    const downloadElement = document.createElement('a');
    
    downloadElement.setAttribute('href', dataStr);
    downloadElement.setAttribute(
      'download',
      filename || `api-conversations-${new Date().toISOString().split('T')[0]}.json`
    );
    
    document.body.appendChild(downloadElement);
    downloadElement.click();
    downloadElement.remove();
  }
  
  /**
   * 从JSON字符串导入数据
   */
  static importFromJson(jsonString: string): DataExport {
    try {
      const data = JSON.parse(jsonString);
      
      // 验证数据格式
      if (!this.validateImportData(data)) {
        throw new Error('无效的导入数据格式');
      }
      
      return data;
    } catch (error) {
      throw new Error(`导入失败: ${error instanceof Error ? error.message : '未知错误'}`);
    }
  }
  
  /**
   * 验证导入数据格式
   */
  static validateImportData(data: any): data is DataExport {
    if (!data || typeof data !== 'object') {
      return false;
    }
    
    if (!data.version || typeof data.version !== 'string') {
      return false;
    }
    
    if (!data.exportedAt || typeof data.exportedAt !== 'string') {
      return false;
    }
    
    // 检查数据结构
    if (data.providers && !Array.isArray(data.providers)) {
      return false;
    }
    
    if (data.sessions && !Array.isArray(data.sessions)) {
      return false;
    }
    
    if (data.templates && !Array.isArray(data.templates)) {
      return false;
    }
    
    return true;
  }
  
  /**
   * 获取导出文件的大小信息
   */
  static getExportSizeInfo(
    providers: ApiProvider[],
    sessions: ChatSession[],
    templates: ChatTemplate[]
  ): {
    providers: number;
    sessions: number;
    templates: number;
    totalItems: number;
    estimatedSize: string;
  } {
    const exportData = this.exportData(providers, sessions, templates);
    const jsonString = JSON.stringify(exportData);
    const sizeInBytes = new Blob([jsonString]).size;
    
    // 格式化文件大小
    const formatSize = (bytes: number): string => {
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    };
    
    return {
      providers: providers.length,
      sessions: sessions.length,
      templates: templates.length,
      totalItems: providers.length + sessions.length + templates.length,
      estimatedSize: formatSize(sizeInBytes),
    };
  }
  
  /**
   * 生成备份建议
   */
  static getBackupRecommendation(
    _providers: ApiProvider[],
    sessions: ChatSession[],
    _templates: ChatTemplate[]
  ): {
    recommended: boolean;
    reason: string;
    suggestion: string;
  } {
    const totalSessions = sessions.length;
    const totalMessages = sessions.reduce((sum, session) => sum + session.messageCount, 0);
    
    let recommended = false;
    let reason = '';
    let suggestion = '';
    
    if (totalSessions >= 50) {
      recommended = true;
      reason = `您有 ${totalSessions} 个对话记录，建议定期备份`;
      suggestion = '建议每周备份一次';
    } else if (totalMessages >= 500) {
      recommended = true;
      reason = `您有 ${totalMessages} 条消息记录，建议定期备份`;
      suggestion = '建议每月备份一次';
    } else if (sessions.some(session => session.tags.includes('重要'))) {
      recommended = true;
      reason = '您有标记为"重要"的对话，建议备份';
      suggestion = '建议立即备份重要对话';
    } else {
      recommended = false;
      reason = '当前数据量较小，可以定期备份';
      suggestion = '建议在数据量增加时开始定期备份';
    }
    
    return { recommended, reason, suggestion };
  }
  
  /**
   * 比较两个导出数据的差异
   */
  static compareExports(export1: DataExport, export2: DataExport): {
    providers: { added: number; removed: number; changed: number };
    sessions: { added: number; removed: number; changed: number };
    templates: { added: number; removed: number; changed: number };
  } {
    const result = {
      providers: { added: 0, removed: 0, changed: 0 },
      sessions: { added: 0, removed: 0, changed: 0 },
      templates: { added: 0, removed: 0, changed: 0 },
    };
    
    // 比较供应商
    const providerIds1 = new Set(export1.providers?.map(p => p.id) || []);
    const providerIds2 = new Set(export2.providers?.map(p => p.id) || []);
    
    // 新增的供应商
    result.providers.added = [...providerIds2].filter(id => !providerIds1.has(id)).length;
    // 删除的供应商
    result.providers.removed = [...providerIds1].filter(id => !providerIds2.has(id)).length;
    
    // 比较会话
    const sessionIds1 = new Set(export1.sessions?.map(s => s.id) || []);
    const sessionIds2 = new Set(export2.sessions?.map(s => s.id) || []);
    
    result.sessions.added = [...sessionIds2].filter(id => !sessionIds1.has(id)).length;
    result.sessions.removed = [...sessionIds1].filter(id => !sessionIds2.has(id)).length;
    
    // 比较模板
    const templateIds1 = new Set(export1.templates?.map(t => t.id) || []);
    const templateIds2 = new Set(export2.templates?.map(t => t.id) || []);
    
    result.templates.added = [...templateIds2].filter(id => !templateIds1.has(id)).length;
    result.templates.removed = [...templateIds1].filter(id => !templateIds2.has(id)).length;
    
    return result;
  }
  
  /**
   * 生成导出报告
   */
  static generateExportReport(
    providers: ApiProvider[],
    sessions: ChatSession[],
    templates: ChatTemplate[]
  ): string {
    const sizeInfo = this.getExportSizeInfo(providers, sessions, templates);
    const backupRec = this.getBackupRecommendation(providers, sessions, templates);
    
    const report = `
# API 对话数据导出报告

## 导出信息
- 导出时间: ${new Date().toISOString()}
- 数据版本: 1.0.0

## 数据统计
- 供应商数量: ${sizeInfo.providers}
- 对话数量: ${sizeInfo.sessions}
- 模板数量: ${sizeInfo.templates}
- 总项目数: ${sizeInfo.totalItems}
- 预估文件大小: ${sizeInfo.estimatedSize}

## 备份建议
- 建议备份: ${backupRec.recommended ? '是' : '否'}
- 原因: ${backupRec.reason}
- 建议: ${backupRec.suggestion}

## 导出说明
1. 此文件包含完整的 API 对话管理系统数据
2. 可以用于数据备份、迁移或分享
3. 请妥善保管此文件，避免数据泄露
4. 支持导入到相同版本的系统

## 安全提示
- API 密钥已加密存储
- 建议定期更换 API 密钥
- 不要在公共场合分享此文件

---
*报告生成时间: ${new Date().toLocaleString('zh-CN')}*
    `.trim();
    
    return report;
  }
}